import { Order } from "@amzn/ito-client";

import { HardwareOrderStatus } from "../interfaces/hardware-models";

export const TERMINAL_STATUSES = new Set<string>([
  HardwareOrderStatus.CANCELLED,
  HardwareOrderStatus.DELIVERED,
  HardwareOrderStatus.REJECTED,
]);

export function isTerminalStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function isCancellationEligible(
  hopOrders: Array<{ identifier: string; status: string }>
): boolean {
  return (
    hopOrders.length === 0 ||
    hopOrders.every((order) => order.status === "ready-for-export")
  );
}

export function isValidCancellationReason(reason: string): boolean {
  return reason.trim().length > 0;
}

/** Helper to get items array from an order */
function getItems(order: Order): any[] {
  return (order as any).items ?? [];
}

/**
 * Finds the item that owns the given itemId. If itemId matches an item directly,
 * returns that item. If itemId matches a sub-line item's id, returns the parent item.
 */
function findItemOrParent(order: Order, itemId: string): any | undefined {
  return getItems(order).find(
    (item) =>
      item.itemId === itemId ||
      (item.subLineItems ?? []).some((sub: any) => sub.id === itemId)
  );
}

/**
 * Checks whether an item (or sub-line item) can be cancelled based on its
 * associated HOP order statuses. For sub-line items, the parent item's HOP
 * orders are checked.
 */
export function isItemCancellationAllowed(order: Order, itemId: string): boolean {
  const item = findItemOrParent(order, itemId);
  if (!item) return true;
  return isCancellationEligible(item?.details?.hopOrders ?? []);
}

/**
 * Returns true if the given itemId (item or sub-line item) is the last
 * non-cancelled item/sub-line item in the order.
 */
export function isLastNonCancelledItem(order: Order, itemId: string): boolean {
  const nonCancelledIds = getItems(order).flatMap((item) => {
    const subs = item.subLineItems ?? [];
    if (subs.length > 0) {
      return subs
        .filter((sub: any) => sub.status !== HardwareOrderStatus.CANCELLED && sub.id)
        .map((sub: any) => sub.id);
    }
    return item.status !== HardwareOrderStatus.CANCELLED && item.itemId ? [item.itemId] : [];
  });

  return nonCancelledIds.length === 1 && nonCancelledIds[0] === itemId;
}

/**
 * Extracts all HOP orders from the order's items details.
 */
export function getHopOrdersFromItems(order: Order): Array<{ identifier: string; status: string }> {
  return getItems(order).flatMap(
    (item) => (item?.details?.hopOrders ?? []).filter((hop: any) => hop.identifier && hop.status)
  );
}
